import React, { FC } from 'react';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { muiCustomSelect, newThemeInput } from '_fields/inputs';
import { words } from '_fields/normalizers';
import { connect, useStore } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { Helmet } from 'react-helmet';
import { BUSINESS_YEARS_DEFAULT_VALUES, Text } from 'shared-components';
import { AddressField } from '@components/Apply/MoreDetailsOnYourBusiness/AddressField';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import {
  ApplyWizardSteps,
  STEP_BCM_2_BUSINESS_MORE_DETAILS,
  STEP_BCM_BUSINESS_MORE_DETAILS,
  STEP_BUSINESS_MORE_DETAILS,
} from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import ensureLoggedIn from '../../../hooks/ensureLoggedIn';
import { BCMShortWizardShell, BCMShortSubmitButton, BCMShortSubmittingOverlay } from '@components/BCMShort/WizardShell';

type MoreDetailsOnYourBusinessProps = {
  loanType: string;
  handleSubmit: () => void;
  invalid: boolean;
  years?: number;
  saving?: boolean;
};

const MoreDetailsOnYourBusinessWrapper: FC<MoreDetailsOnYourBusinessProps> = (props) => {
  const auth0 = useAuth0();

  if (auth0.isLoading) {
    return (
      <BCMShortWizardShell currentStepIndex={1}>
        <div className="flex items-center justify-center w-full py-[60px]">
          <Spinner />
        </div>
      </BCMShortWizardShell>
    );
  }

  return <MoreDetailsOnYourBusinessMainComponent {...props} />;
};

const MoreDetailsOnYourBusinessMainComponent: FC<MoreDetailsOnYourBusinessProps> = ({
  handleSubmit,
  invalid,
  saving: savingOnSubmit,
}) => {
  const { saving } = useApplyWizardContext();
  const form = useForm();

  const store = useStore();
  const latestValues = getFormValues('application')(store.getState() as RootState) || {};
  const validationOutput = reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema)(latestValues);

  const isSubmitting = !!(saving || savingOnSubmit);

  if (isSubmitting) {
    return <BCMShortSubmittingOverlay currentStepIndex={1} />;
  }

  return (
    <BCMShortWizardShell currentStepIndex={1}>
      <Helmet>
        <title>More Details on Your Business</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          A Few More Details
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Business Information
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  COMPANY DBA (OPTIONAL)
                </span>
                <Field
                  name="business_dba"
                  component={newThemeInput}
                  type="text"
                  placeholder="Company DBA (Optional)"
                  normalize={words}
                  label="Company DBA (Optional)"
                  data-testid="business_dba"
                  autoFocus
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  BUSINESS ADDRESS*
                </span>
                {/* Address.js hardcodes `m-0` on the Geosuggest wrapper which
                    cancels auto-margin centering. `!mx-auto` (with the !
                    important modifier) wins the tailwind-merge cascade and
                    centers the 368px-capped search field like the other
                    inputs above. */}
                <AddressField
                  autoFocus={false}
                  variant="transparent-black"
                  getManualAddressText={(isManualAddress: boolean) => {
                    return isManualAddress ? 'Use Search Tool' : 'Enter Manually';
                  }}
                  toggleManualAddressClassName="uppercase"
                  geoSuggestClassName="!mx-auto"
                  geoSuggestInputClassName="!bg-dawn3"
                  addressContainerProps={{ className: 'mx-auto' }}
                  addressStreetProps={{ className: '!bg-dawn3' }}
                  addressSuiteProps={{ className: '!bg-dawn3' }}
                  addressCityProps={{ className: '!bg-dawn3' }}
                  addressStateProps={{
                    className: '!bg-dawn3',
                    selectTriggerProps: { className: '!bg-dawn3' },
                  }}
                  addressPostalCodeProps={{ className: '!bg-dawn3' }}
                />
                {(latestValues as any).business_city && (validationOutput as any).business_street && (
                  <Text variant="error">Please enter a valid street address</Text>
                )}
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  FEDERAL TAX ID*
                </span>
                <Field
                  data-testid="fed-tax-id"
                  name="business_ein"
                  component={newThemeInput}
                  type="tel"
                  label="Tax ID"
                  placeholder="Federal Tax ID* (XX-XXXXXXX)"
                  formatAsFederalTaxId
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  TIME IN BUSINESS*
                </span>
                <Field
                  name="business_years"
                  component={muiCustomSelect}
                  label="Time in Business*"
                  className="!bg-dawn3"
                  data-testid="business_years"
                  selectValueProps={{ placeholder: 'Time in Business*', className: '!bg-dawn3' }}
                  selectTriggerProps={{ className: '!bg-dawn3' }}
                  valueAsNumber
                  options={BUSINESS_YEARS_DEFAULT_VALUES}
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto text-left">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  YOUR POSITION IN COMPANY*
                </span>
                <Field
                  data-testid={`owner-1-title`}
                  name={`owner_1_title`}
                  component={newThemeInput}
                  type="text"
                  normalize={words}
                  label="Your Position in Company*"
                  className="!bg-dawn3"
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <BCMShortSubmitButton
                  invalid={
                    invalid || (validationOutput as any).business_street || (validationOutput as any).business_city
                  }
                  saving={isSubmitting}
                  data-testid="info_continue"
                >
                  Confirm
                </BCMShortSubmitButton>
                <Text variant="body-form-label" className="!text-[14px] !leading-[18px] !font-condensed">
                  *No hard checks to your credit
                </Text>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </BCMShortWizardShell>
  );
};

const MoreDetailsOnYourBusinessWrapper2 = reduxForm<MoreDetailsOnYourBusinessProps, MoreDetailsOnYourBusinessProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_BCM_BUSINESS_MORE_DETAILS].zodSchema),
})(MoreDetailsOnYourBusinessWrapper);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    years: appSelector(state, 'business_years'),
  };
};

export const BCMShortMoreDetailsOnYourBusiness = ensureLoggedIn(
  withOnSubmit(connect(mapStateToProps)(MoreDetailsOnYourBusinessWrapper2), STEP_BCM_2_BUSINESS_MORE_DETAILS),
  { requireBusinessId: true }
);
